import React from "react";
import { View } from "react-native";
import { Ionicons } from "@expo/vector-icons";
import Text from "@/components/shared/AppText";
import { useIsMobileUX } from "@/components/hooks/useIsMobileUX";
import { t } from "@/localization";
import tw from "twrnc";

// replayFor: the length picked in the paywall test's one-line picker
// ("1 year"), shown on the replay line; null on today's paywall.
// cameras: how many cameras the purchase covers (a camera group's checkout).
// features: a list to show instead (the paywall for cameras only).
const FeaturesList = ({
  streamDate,
  liveDays = 1,
  replayFor = null,
  cameras = 1,
  features: ownFeatures = null,
}) => {
  const normalizedLiveDays = Number.isFinite(Number(liveDays))
    ? Math.max(Math.round(Number(liveDays)), 1)
    : 1;

  const features = ownFeatures || [
    ...(cameras > 1
      ? [
          {
            icon: "videocam-outline",
            description: t("{count} camera angles on one link", {
              count: cameras,
            }),
            benefits: t("Guests switch between the cameras in the player."),
          },
        ]
      : []),
    ...(streamDate
      ? [
          {
            icon: "radio-outline",
            description: t("Go live on {date}", { date: streamDate }),
            benefits: t("Your event link is ready the moment you unlock it."),
          },
        ]
      : []),
    {
      icon: "sunny-outline",
      description:
        normalizedLiveDays > 1
          ? t("Go Live for {count} days", { count: normalizedLiveDays })
          : t("All-Day HD broadcasting"),
      benefits:
        normalizedLiveDays > 1
          ? t("Pause and resume stream to the same link across all event days.")
          : t(
              "Pause and Resume stream to the same link. Immersive audience experience."
            ),
    },
    {
      icon: "download-outline",
      description: replayFor
        ? t("Replay & download for {length}", { length: replayFor })
        : t("Replay & download"),
      benefits: replayFor
        ? t("Kept online for {length}. You can change it below.", {
            length: replayFor,
          })
        : t("Kept online for the time you choose below"),
    },
    {
      icon: "people-outline",
      description: t("Unlimited guests"),
      benefits: t(
        "Bring in as many viewers as you like, without any restrictions."
      ),
    },
    {
      icon: "notifications-outline",
      description: t("Reminders for guests"),
      benefits: t("Timely reminders, so no one misses out."),
    },
  ];

  const isMobileUX = useIsMobileUX();

  return (
    <View style={tw`mt-1 mb-2`}>
      <View>
        {features.map((feature, index) => (
          <View
            key={index}
            style={tw.style(
              "flex-row items-center",
              isMobileUX ? "my-0.5" : "my-4"
            )}
          >
            <View
              style={tw.style(
                "mr-3 items-center justify-center rounded-[10px] bg-[#FFF2ED]",
                isMobileUX ? "w-[34px] h-[34px]" : "w-[44px] h-[44px]"
              )}
            >
              <Ionicons
                name={feature.icon}
                size={isMobileUX ? 20 : 26}
                color="#F05A3C"
              />
            </View>
            <View style={tw`flex-1 justify-center`}>
              <Text
                style={tw`${
                  isMobileUX ? "text-sm" : "text-base"
                } font-semibold text-[#1E3A52]`}
              >
                {feature.description}
              </Text>
              {!isMobileUX && (
                <Text style={tw`mt-1 text-sm text-[#6B7A8A]`}>
                  {feature.benefits}
                </Text>
              )}
            </View>
          </View>
        ))}
      </View>
    </View>
  );
};

export default FeaturesList;
